import React, { useState, useEffect } from 'react';
import './IssueBook.css';
import * as Constants from '../../Constant/constant';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import SearchableSelect from '../../shared/SearchableSelect';

function IssueBook() {
  const [libraries, setLibraries] = useState([]);
  const [books, setBooks] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedLibrary, setSelectedLibrary] = useState('');
  const [selectedBookId, setSelectedBookId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [issuedData, setIssuedData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    fetchLibraries();
    const today = new Date();
    const due = new Date();
    due.setDate(today.getDate() + 14);
    setIssueDate(today.toISOString().split('T')[0]);
    setDueDate(due.toISOString().split('T')[0]);
  }, []);

  const fetchLibraries = async () => {
    const res = await fetch(Constants.API_PATH + '/add_new_library_data');
    const data = await res.json();
    setLibraries(data);
  };

  const fetchBooksAndUsers = async (libraryId) => {
    setSelectedLibrary(libraryId);
    try {
      const booksRes = await fetch(`${Constants.API_PATH}/available-books-by-library?library_id=${libraryId}`);
      const usersRes = await fetch(`${Constants.API_PATH}/register_user_school_id/${libraryId}`);
      const issuedRes = await fetch(`${Constants.API_PATH}/get-book-issue-status?library_id=${libraryId}`);

      const booksData = await booksRes.json();
      const usersData = await usersRes.json();
      const issuedData = await issuedRes.json();

      setBooks(booksData || []);
      setUsers(usersData || []);
      setIssuedData(issuedData || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleIssueBook = async () => {
    const isAlreadyIssued = issuedData.some(
      issue => issue.book_code === books.find(b => b.id === parseInt(selectedBookId))?.book_code &&
               issue.return_book === 0 &&
               issue.cancel_book === 0 &&
               issue.status === 0
    );

    if (!selectedUserId || !selectedBookId) {
      alert('Please select both book and user.');
      return;
    }

    if (isAlreadyIssued) {
      alert('❌ This book is already issued and not returned!');
      return;
    }

    try {
      const res = await fetch(Constants.API_PATH + '/issue-book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedUserId,
          book_id: selectedBookId,
          issue_date: issueDate,
          due_date: dueDate
        })
      });
      const result = await res.json();

      if (result.success) {
        alert('✅ Book issued successfully!');
        fetchBooksAndUsers(selectedLibrary);
        setSelectedBookId('');
        setSelectedUserId('');
      } else {
        alert('❌ Issue failed: ' + result.message);
      }
    } catch (error) {
      console.error('Error issuing book:', error);
    }
  };

  const handleReturnBook = async (bookId, userId) => {
    const confirm = window.confirm('Are you sure you want to return this book?');
    if (!confirm) return;
  
    try {
      const res = await fetch(`${Constants.API_PATH}/return-book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_id: bookId,
          user_id: userId,
          return_date: new Date().toISOString().split('T')[0]
        })
      });
  
      const result = await res.json();
      if (result.success) {
        alert(result.message);
        fetchBooksAndUsers(selectedLibrary); // refresh list
      } else {
        alert('❌ Failed: ' + result.message);
      }
    } catch (error) {
      console.error('Return error:', error);
      alert('❌ Error occurred during return.');
    }
  };
  
  

  const filteredData = issuedData.filter(item =>
    item.book_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.student_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const calculateDaysLeft = (dueDate) => {
    if (!dueDate) return '-';
    const today = new Date();
    const due = new Date(dueDate);
    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? `${diff} days` : `Overdue`;
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(issuedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'IssuedBooks');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(fileData, 'IssuedBooks.xlsx');
  };

  return (
    <div className="issue-book-section containernew">
      <h2>📚 Issue Book</h2>

      <div className="form-section">
        <div className="form-group">
          <label>Select Library</label>
          <SearchableSelect
            value={selectedLibrary}
            onChange={(e) => fetchBooksAndUsers(e.target.value)}
            options={libraries.map(lib => ({ value: lib.id, label: lib.library_name }))}
            placeholder="Select Library"
          />
        </div>

        <div className="form-group">
          <label>Select Book</label>
          <SearchableSelect
            value={selectedBookId}
            onChange={(e) => setSelectedBookId(e.target.value)}
            options={books.map(book => ({ value: book.book_id, label: book.book_name }))}
            placeholder="Select Book"
          />
        </div>

        <div className="form-group">
          <label>Select User Name</label>
          <SearchableSelect
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            options={users.map(user => ({ value: user.id, label: `${user.first_name} ${user.last_name}` }))}
            placeholder="Select User Name"
          />
        </div>

        <button className="btn btn-primary mt-3" onClick={handleIssueBook}>✅ Issue Book</button>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-4">
        <input
          type="text"
          className="form-control w-50"
          placeholder="Search by book or student name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button className="btn btn-success ms-2" onClick={exportToExcel}>⬇️ Export to Excel</button>
      </div>

      <h4 className="mt-4">📄 Issued Books List</h4>
      <table className="custom-table">
        <thead>
          <tr>
            <th>Student Name</th>
            <th>Email</th>
            <th>Book</th>
            <th>Issue Date</th>
            <th>Due Date</th>
            {/* <th>Days Left</th> */}
            <th>Return Date</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item, index) => (
            <tr key={index}>
              <td>{item.student_name || ''}</td>
              <td>{item.email || ''}</td>
              <td>{item.book_name}</td>
              <td>{item.issue_date ? new Date(item.issue_date).toLocaleDateString() : ''}</td>
              <td>{item.due_date ? new Date(item.due_date).toLocaleDateString() : ''}</td>
              {/* <td>{calculateDaysLeft(item.due_date)}</td> */}
              <td>{item.return_date ? new Date(item.return_date).toLocaleDateString() : ''}</td>
              <td>{
                item.return_book ? 'Returned' :
                item.cancel_book ? 'Cancelled' :
                item.status === 0 ? 'Issued' :
                (!item.student_name && !item.issue_date ? 'Not Issued' : 'Unknown')
              }</td>
            <td>
                {item.status === 0 && item.return_book === 0 ? (
                    <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleReturnBook(item.book_id, item.user_id)}
                    >
                    Return Book
                    </button>
                ) : (
                    <span className="text-muted">—</span>
                )}
                </td>

            </tr>
          ))}
        </tbody>
      </table>

      <nav className="pagination justify-content-center mt-3">
        <ul className="pagination">
          {Array.from({ length: totalPages }).map((_, i) => (
            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

export default IssueBook;